import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Alert, Button, Card, Input, Select, Space, Spin, Typography, message } from 'antd'
import Popconfirm from '../components/Popconfirm'
import * as api from '../lib/api'
import { approverName } from './HRDashboard'
import AppraisalDetail from './AppraisalDetail'
import AppraisalConversationSidebar from './AppraisalConversationSidebar'
import ManagerFeedback from './ManagerFeedback'
import { useFormStructure } from './useFormStructure'
import { getConfiguredRatingDisplay, getRatingValueDisplay } from './ratingScale'
import { unresolvedReturnForStatus } from './ReturnHistory'

const { Text } = Typography
const { TextArea } = Input

export default function ApproverSignoffPage({ level }) {
  const { id } = useParams()
  const navigate = useNavigate()
  const [appraisal, setAppraisal] = useState(null)
  const [currentUser, setCurrentUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [redirecting, setRedirecting] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [comments, setComments] = useState('')
  const [resubmissionRemarks, setResubmissionRemarks] = useState('')
  const [returnTarget, setReturnTarget] = useState('previous')
  const [discussionOpen, setDiscussionOpen] = useState(false)
  const [returnComments, setReturnComments] = useState('')
  const [returning, setReturning] = useState(false)
  const { formStructure, formConfig } = useFormStructure(appraisal?.session_template || appraisal?.session_business_unit)

  useEffect(() => {
    Promise.all([api.getAppraisal(id), api.getMe()])
      .then(([data, user]) => {
        if (Number(data[`approver_${level}`]) !== Number(user.id)) {
          setRedirecting(true)
          navigate('/dashboard', { replace: true })
          return
        }
        setAppraisal(data)
        setDiscussionOpen(Boolean(data.return_comments || data.return_history?.length))
        const statuses = { 2: 'pending_head', 3: 'pending_bu_head', 4: 'pending_approver_4', 5: 'pending_approver_5' }
        const activeReturn = unresolvedReturnForStatus(data.return_history, statuses[level])
        setReturnTarget(activeReturn || (!data.return_history?.length && data.return_comments) ? 'approver' : 'previous')
        setCurrentUser(user)
        const commentsByLevel = {
          2: data.head_comments,
          3: data.bu_head_comments,
          4: data.approver_4_comments,
          5: data.approver_5_comments
        }
        setComments(commentsByLevel[level] || '')
      })
      .finally(() => setLoading(false))
  }, [id, level, navigate])

  if (loading || redirecting) return <div style={{ textAlign: 'center', padding: 60 }}><Spin size='large' /></div>
  if (!appraisal) return <Alert type='error' message='Appraisal not found' />

  const firstApproverName = approverName(appraisal, 1)
  const currentApproverName = approverName(appraisal, level)
  const previousLevel = [level - 1, level - 2, level - 3, 1]
    .find(candidate => candidate >= 1 && Boolean(appraisal[`approver_${candidate}`])) || 1
  const previousApproverName = approverName(appraisal, previousLevel) || firstApproverName
  const nextLevel = [level + 1, level + 2]
    .find(candidate => candidate <= 5 && Boolean(appraisal[`approver_${candidate}`]))
  const nextApproverName = nextLevel ? approverName(appraisal, nextLevel) : null
  const statusByLevel = {
    2: 'pending_head',
    3: 'pending_bu_head',
    4: 'pending_approver_4',
    5: 'pending_approver_5'
  }
  const activeReturn = unresolvedReturnForStatus(
    appraisal.return_history,
    statusByLevel[level]
  )
  const canReview = appraisal.status === statusByLevel[level] &&
    Number(appraisal[`approver_${level}`]) === Number(currentUser?.id)
  const isReturnedForRevision = canReview && Boolean(
    activeReturn || (!appraisal.return_history?.length && appraisal.return_comments)
  )
  const returningApproverName = activeReturn?.actor_name ||
    appraisal.returned_by_name || appraisal.returned_by_email || 'the returning approver'

  const submit = async () => {
    if (isReturnedForRevision && !resubmissionRemarks.trim()) {
      setReturnTarget('approver')
      setDiscussionOpen(true)
      message.error('A response to the returning approver is required.')
      return
    }
    setSubmitting(true)
    try {
      if (level === 2) {
        await api.headSignoff(id, {
          head_comments: comments,
          resubmission_remarks: resubmissionRemarks.trim()
        })
      } else if (level === 3) {
        await api.buHeadSignoff(id, {
          bu_head_comments: comments,
          resubmission_remarks: resubmissionRemarks.trim()
        })
      } else {
        await api.additionalApproverSignoff(id, level, {
          comments,
          resubmission_remarks: resubmissionRemarks.trim()
        })
      }
      navigate('/dashboard')
    } catch (error) {
      message.error(error.message)
    } finally {
      setSubmitting(false)
    }
  }

  const submitReturn = async () => {
    if (!returnComments.trim()) {
      message.error('A return reason is required.')
      return
    }
    setReturning(true)
    try {
      const commentField = level === 2
        ? 'head_comments'
        : level === 3 ? 'bu_head_comments' : `approver_${level}_comments`
      const review = { [commentField]: comments }
      if (returnTarget === 'employee') {
        await api.returnAppraisalToEmployee(id, returnComments.trim(), review)
      } else {
        await api.returnAppraisalToPreviousStage(id, returnComments.trim(), review)
      }
      navigate('/dashboard')
    } catch (error) {
      message.error(error.message)
    } finally {
      setReturning(false)
    }
  }

  const busy = submitting || returning
  const replyingToApprover = returnTarget === 'approver'
  const returnRecipient = replyingToApprover ? returningApproverName : returnTarget === 'employee' ? 'Employee' : previousApproverName
  const formData = appraisal.form_data || {}
  const precedingComments = [
    [2, appraisal.head_comments], [3, appraisal.bu_head_comments], [4, appraisal.approver_4_comments]
  ].filter(([reviewLevel, comment]) => reviewLevel < level && comment)

  return (
    <div className='tti-appraisal-layout'>
      <div className='tti-appraisal-form' style={{ flex: 1, minWidth: 0 }}>
        <AppraisalDetail
          appraisal={appraisal}
          approvalHistoryMinLevel={previousLevel}
          approvalHistoryMaxLevel={previousLevel}
          showReturnContext={false}
        >
          <div style={{ marginTop: 24, paddingBottom: 96 }}>
            <Card className='tti-appraisal-section-card' title={<Text strong>Comments & Sign-off - {currentApproverName}</Text>}>
              <TextArea
                rows={4}
                value={comments}
                onChange={event => setComments(event.target.value)}
                disabled={!canReview || busy}
                placeholder={`Add comments by ${currentApproverName}`}
              />
            </Card>
          </div>
          {canReview && (
            <div className='tti-form-action-bar tti-appraisal-action-bar'>
              <div className='tti-form-action-bar__inner'>
                <Button className='tti-btn-secondary tti-form-action-bar__cancel' disabled={busy} onClick={() => navigate('/dashboard')}>Cancel</Button>
                <Space wrap>
                  <Popconfirm
                    title='Are you sure?'
                    description='Please confirm you want to submit.'
                    okText='Approve'
                    cancelText='Cancel'
                    onConfirm={submit}
                    disabled={busy}
                  >
                    <Button type='primary' loading={submitting} disabled={busy} style={{ color: '#FFFFFF' }}>
                      {nextApproverName ? `Approve & Send to ${nextApproverName}` : 'Approve & Complete'}
                    </Button>
                  </Popconfirm>
                </Space>
              </div>
            </div>
          )}
        </AppraisalDetail>
      </div>
      <AppraisalConversationSidebar
        appraisal={appraisal}
        open={discussionOpen}
        onOpen={() => setDiscussionOpen(true)}
        onClose={() => setDiscussionOpen(false)}
        feedback={(
          <>
            <ManagerFeedback
              appraisal={appraisal}
              formData={formData}
              objectives={formData.objectives?.length ? formData.objectives : (formData.annual_objectives || [])}
              formStructure={formStructure}
              formConfig={formConfig}
              ratingDisplay={value => formConfig.rating_options?.length ? getConfiguredRatingDisplay(value, formConfig) : getRatingValueDisplay(value, appraisal)}
            />
            {precedingComments.map(([reviewLevel, comment]) => (
              <Card key={reviewLevel} size='small' title={`Comments by ${approverName(appraisal, reviewLevel)}`}>
                <Text style={{ whiteSpace: 'pre-wrap' }}>{comment}</Text>
              </Card>
            ))}
          </>
        )}
        composer={canReview ? (
          <div>
            <Select
              aria-label='Comment recipient'
              value={returnTarget}
              onChange={setReturnTarget}
              disabled={busy}
              style={{ width: '100%', marginBottom: 12 }}
              options={[
                ...(isReturnedForRevision ? [{ value: 'approver', label: `Returning approver: ${returningApproverName}` }] : []),
                { value: 'previous', label: previousApproverName },
                ...(appraisal.can_return_to_employee ? [{ value: 'employee', label: 'Employee' }] : [])
              ]}
            />
            <Text strong style={{ display: 'block', marginBottom: 8 }}>Feedback to {returnRecipient}</Text>
            <TextArea
              aria-label={`Feedback to ${returnRecipient}`}
              rows={3}
              value={replyingToApprover ? resubmissionRemarks : returnComments}
              onChange={event => replyingToApprover ? setResubmissionRemarks(event.target.value) : setReturnComments(event.target.value)}
              disabled={busy}
              placeholder='Please Enter'
            />
          </div>
        ) : null}
        actions={canReview ? (
          <Button type='link' loading={busy} disabled={busy} onClick={replyingToApprover ? submit : submitReturn}>Send</Button>
        ) : null}
      />
    </div>
  )
}
